import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  DESCRIPTION_LIMIT,
  LAYOUT_NAME_REGEX,
  TITLE_LIMIT,
} from '@pages/IntroducingBroker/DashboardPoC/types'

import type { BaseModalType } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { Input } from '@ui/components/Input'
import { Textarea } from '@ui/components/Input/Textarea/Textarea'
import { Modal } from '@ui/components/Modal'

import { Box } from '@shared/components/Box'

type EditLayoutMetadataModalProps = {
  onSave: (name: string, description: string) => void
  currentName: string
  currentDescription: string
}

export const EditLayoutMetadataModal: BaseModalType<EditLayoutMetadataModalProps> = ({
  onSave,
  currentName,
  currentDescription,
  close,
}) => {
  const { t } = useTranslation()

  const [layoutName, setLayoutName] = useState(currentName)
  const [layoutDescription, setLayoutDescription] = useState(currentDescription)
  const [nameError, setNameError] = useState('')
  const [descriptionError, setDescriptionError] = useState('')

  useEffect(() => {
    setLayoutName(currentName)
    setLayoutDescription(currentDescription)
    setNameError('')
    setDescriptionError('')
  }, [currentName, currentDescription])

  const handleSave = () => {
    if (!layoutName.trim()) {
      setNameError(t('Dashboard.LayoutNameRequired'))
      return
    }

    if (layoutName.length > TITLE_LIMIT) {
      setNameError(t('Dashboard.LayoutTitleMaxLength', { count: TITLE_LIMIT }))
      return
    }

    if (layoutDescription.length > DESCRIPTION_LIMIT) {
      setDescriptionError(t('Dashboard.LayoutDescriptionMaxLength', { limit: DESCRIPTION_LIMIT }))
      return
    }

    if (!LAYOUT_NAME_REGEX.test(layoutName)) {
      setNameError(t('Dashboard.LayoutNameInvalid'))
      return
    }

    onSave(layoutName, layoutDescription)
    close()
  }

  const handleClose = () => {
    setLayoutName(currentName)
    setLayoutDescription(currentDescription)
    setNameError('')
    setDescriptionError('')
    close()
  }

  return (
    <Modal>
      <Modal.Header canClose>{t('Dashboard.EditLayoutSettings')}</Modal.Header>

      <Modal.Body>
        <Box fullWidth fullHeight>
          <Box mb='x16'>
            <Input
              label={t('Dashboard.LayoutNameLabel')}
              value={layoutName}
              onChange={(e) => {
                setLayoutName(e.target.value)
                setNameError('')
              }}
              maxLength={TITLE_LIMIT}
              fullWidth
              error={Boolean(nameError)}
              hint={nameError}
            />
            <Body2 color='text-med-em'>
              {layoutName.length}/{TITLE_LIMIT}
            </Body2>
          </Box>

          <Box mb='x16'>
            <Textarea
              label={t('Dashboard.LayoutDescriptionLabel')}
              value={layoutDescription}
              onChange={(e) => {
                setLayoutDescription(e.target.value)
                setDescriptionError('')
              }}
              maxLength={DESCRIPTION_LIMIT}
              rows={2}
              fullWidth
              maxHeight='150px'
              error={Boolean(descriptionError)}
              hint={descriptionError}
            />

            <Body2 color='text-med-em'>
              {layoutDescription.length}/{DESCRIPTION_LIMIT}
            </Body2>
          </Box>
        </Box>
      </Modal.Body>

      <Modal.Footer>
        <Modal.Footer.Secondary onClick={handleClose}>
          {t('Dashboard.Cancel')}
        </Modal.Footer.Secondary>

        <Modal.Footer.Primary onClick={handleSave}>
          {t('Dashboard.SaveChanges')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
